import { useEffect, useRef, useState } from "react";
import {
  useResourcePersonCourses,
  useResourcePersonSubmissions,
} from "../store/resourcePerson/resourcePersonStore";

/**
 * Count of submissions still waiting to be graded across the caller's courses.
 * Used as the Assessment sidebar badge. Reads each course's paginated
 * submissions with `status=PENDING&limit=1` and sums `total`, without touching
 * the shared submissions slice used by the grading pages.
 */
export default function useAssessmentBadge() {
  const { courses, coursesLoading, fetchCourses } = useResourcePersonCourses();
  const { fetchPendingSubmissionCount } = useResourcePersonSubmissions();
  const [count, setCount] = useState(0);
  const running = useRef(false);
  const requestedCourses = useRef(false);

  useEffect(() => {
    if (coursesLoading || requestedCourses.current) return;
    if (!Array.isArray(courses) || courses.length === 0) {
      requestedCourses.current = true;
      fetchCourses({ page: 1, limit: 100 });
    }
  }, [courses, coursesLoading, fetchCourses]);

  const courseIds = (Array.isArray(courses) ? courses : [])
    .map((course) => course?.id)
    .filter(Boolean)
    .join(",");

  useEffect(() => {
    if (!courseIds) {
      setCount(0);
      return undefined;
    }
    let active = true;

    const load = async () => {
      if (running.current) return;
      running.current = true;
      try {
        const results = await Promise.all(
          courseIds.split(",").map((id) => fetchPendingSubmissionCount(id))
        );
        if (active) {
          setCount(results.reduce((sum, result) => sum + (result?.count || 0), 0));
        }
      } finally {
        running.current = false;
      }
    };

    load();
    const timer = setInterval(load, 60 * 1000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [courseIds, fetchPendingSubmissionCount]);

  return { count, loading: coursesLoading };
}
